import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCountries } from "@/lib/countries";
import { COMPANY_TYPES } from "@/pages/SettingsPage";
import { toast } from "sonner";
import { CheckCircle2, Sparkles, Loader2 } from "lucide-react";

/**
 * Self-service trial signup. Mirrors the Founding Customer Programme:
 * - Trial status and founding-customer membership are set by the backend
 *   (`/auth/trial-signup`). Standard trial is 14 days; the first 10
 *   founding companies get 3 months free instead. The frontend just
 *   collects the form.
 *
 * The page renders two cosmetic variants driven by `?founder=1`:
 *   - Standard view: "Start your free 14-day trial."
 *   - Founder view:  "Claim your founding spot."
 * Both variants POST to the same `/auth/trial-signup` endpoint; the
 * backend independently awards a founder spot when one is available.
 */
export default function TrialSignupPage() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const countries = useCountries();
  const [searchParams] = useSearchParams();
  const isFounder = searchParams.get("founder") === "1";
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", password: "",
    company_name: "", country: "United Kingdom", company_type: "",
    plan_slug: "starter",  // R5.a - trial plan chooser default
  });
  const [busy, setBusy] = useState(false);

  const update = (k, v) => setForm(s => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (!form.company_type) { toast.error("Please pick the option that best fits your business."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/trial-signup", form);
      setUser(data);
      toast.success("Welcome to NextRota - your trial is live!");
      navigate("/app");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white overflow-x-hidden">
      <PublicNav />

      <section className="flex-1 max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-16 grid md:grid-cols-2 gap-10 items-start">
        {/* LEFT - sell */}
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-[#DBE7FE] text-[#1D4ED8] px-3 py-1 text-xs font-semibold mb-5"
               data-testid={isFounder ? "trial-signup-mode-founder" : "trial-signup-mode-standard"}>
            <Sparkles className="h-3.5 w-3.5"/> {isFounder ? "Founding Customer Programme" : "Free trial"}
          </div>
          {isFounder ? (
            <>
              <h1 className="font-display text-3xl sm:text-4xl text-[#0B1A36]"
                  data-testid="trial-signup-heading-founder">
                Claim your founding spot.
              </h1>
              <p className="text-base text-[#4B5773] mt-3 max-w-md">
                3 months of full founder access, no credit card required. You&apos;ll land straight in your workspace as Workspace Admin.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-[#1F2A44]" data-testid="trial-signup-bullets-founder">
                {[
                  "Limited to the first 10 founding companies",
                  "3 months of full Command-level access",
                  "No credit card required",
                  "Full feature access during your founder period",
                  "After founder access, choose a plan that suits you",
                ].map(t => (
                  <li key={t} className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0"/>{t}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-[#4B5773]">
                Not a founder? <Link to="/start" className="text-[#2563EB] underline">Start a 14-day free trial instead</Link>.
              </p>
            </>
          ) : (
            <>
              <h1 className="font-display text-3xl sm:text-4xl text-[#0B1A36]"
                  data-testid="trial-signup-heading-standard">
                Start your free 14-day trial.
              </h1>
              <p className="text-base text-[#4B5773] mt-3 max-w-md">
                Self-service signup, no credit card required. You&apos;ll land straight in your workspace as Workspace Admin.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-[#1F2A44]" data-testid="trial-signup-bullets-standard">
                {[
                  "Full Command access for 14 days, no credit card required",
                  "First 10 founding companies get 3 months free instead",
                  "Try Automation, RMI Assistant, Coverage Guard and Performance Focus during your trial",
                  "After your trial, choose the plan that fits your team",
                  "Cancel anytime, we'll never charge you without permission",
                ].map(t => (
                  <li key={t} className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0"/>{t}
                  </li>
                ))}
              </ul>
            </>
          )}
          <p className="mt-8 text-xs text-[#4B5773]">
            Already have an account? <Link to="/login" className="text-[#2563EB] underline">Sign in</Link>
          </p>
        </div>

        {/* RIGHT - form */}
        <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm space-y-4"
              data-testid="trial-signup-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="eyebrow">First name</Label>
              <Input required value={form.first_name} data-testid="trial-firstname-input"
                onChange={(e)=>update("first_name", e.target.value)} className="mt-1.5 h-11 rounded-lg"/>
            </div>
            <div>
              <Label className="eyebrow">Last name</Label>
              <Input required value={form.last_name} data-testid="trial-lastname-input"
                onChange={(e)=>update("last_name", e.target.value)} className="mt-1.5 h-11 rounded-lg"/>
            </div>
          </div>
          <div>
            <Label className="eyebrow">Work email</Label>
            <Input required type="email" value={form.email} data-testid="trial-email-input"
              onChange={(e)=>update("email", e.target.value)} className="mt-1.5 h-11 rounded-lg"/>
          </div>
          <div>
            <Label className="eyebrow">Password</Label>
            <Input required type="password" minLength={8} value={form.password} data-testid="trial-password-input"
              onChange={(e)=>update("password", e.target.value)} className="mt-1.5 h-11 rounded-lg"/>
            <p className="text-[11px] text-[#4B5773] mt-1">Use at least 8 characters, including a lowercase letter, an uppercase letter, a number and a special character such as ! @ # $ % ? or *.</p>
          </div>
          <div>
            <Label className="eyebrow">Company name</Label>
            <Input required value={form.company_name} data-testid="trial-company-input"
              onChange={(e)=>update("company_name", e.target.value)} className="mt-1.5 h-11 rounded-lg"/>
          </div>
          <div>
            <Label className="eyebrow">What best describes your business?</Label>
            <Select value={form.company_type} onValueChange={(v)=>update("company_type", v)}>
              <SelectTrigger data-testid="trial-companytype-select" className="mt-1.5 h-11 rounded-lg">
                <SelectValue placeholder="Pick the closest fit"/>
              </SelectTrigger>
              <SelectContent>
                {COMPANY_TYPES.map(t => (
                  <SelectItem key={t.code} value={t.code} data-testid={`trial-companytype-${t.code}`}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-[#4B5773] mt-1">We use this to suggest the right categories and patterns for your team.</p>
          </div>
          <div>
            <Label className="eyebrow">Country</Label>
            <Select value={form.country} onValueChange={(v)=>update("country", v)}>
              <SelectTrigger data-testid="trial-country-select" className="mt-1.5 h-11 rounded-lg"><SelectValue/></SelectTrigger>
              <SelectContent>
                {(countries || []).map(c => <SelectItem key={c.code || c.name} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* R5.a (Feb 2026) - Trial plan chooser. Controls the
              employee cap during the 14-day trial only. All trials
              still include full Command feature access, matching the
              public promise. Founding customers skip this block; they
              use unlimited Founding. Command tile submits the internal
              slug `business` but never displays it. */}
          {!isFounder && (
            <div data-testid="trial-plan-chooser">
              <Label className="eyebrow">Choose your trial size</Label>
              <p className="text-[11px] text-[#4B5773] mt-1">
                All trials include full Command access for 14 days. Choose
                the employee limit you want to start with. You can change
                this during your trial.
              </p>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2"
                   data-testid="trial-plan-chooser-tiles">
                {[
                  { slug: "starter",  title: "Starter trial",
                    body: "Up to 10 active employees" },
                  { slug: "growth",   title: "Growth trial",
                    body: "Up to 30 active employees" },
                  { slug: "business", title: "Command trial",
                    body: "Up to 75 active employees" },
                ].map((opt) => {
                  const active = form.plan_slug === opt.slug;
                  return (
                    <button
                      key={opt.slug}
                      type="button"
                      onClick={() => update("plan_slug", opt.slug)}
                      data-testid={`trial-plan-tile-${opt.slug}`}
                      className={[
                        "text-left rounded-lg border px-3 py-2 transition-colors",
                        active
                          ? "border-[#2563EB] bg-[#EFF3FB]"
                          : "border-slate-200 bg-white hover:border-[#2563EB]/40",
                      ].join(" ")}
                    >
                      <p className="text-[13px] font-semibold text-[#0B1A36]">
                        {opt.title}
                      </p>
                      <p className="text-[11px] text-[#4B5773] mt-1">
                        {opt.body}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <Button type="submit" disabled={busy} data-testid="trial-submit-btn"
            className="w-full h-11 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] font-semibold">
            {busy
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin"/>Creating your workspace…</>
              : (isFounder ? "Claim Founding Spot" : "Start Free Trial")}
          </Button>
          <p className="text-[11px] text-[#4B5773] text-center">
            By signing up you agree to our <Link to="/terms" className="text-[#2563EB] underline">Terms</Link>{" "}
            and <Link to="/privacy" className="text-[#2563EB] underline">Privacy Policy</Link>.
          </p>
        </form>
      </section>

      <Footer />
    </div>
  );
}
